// The photographer's search grammar, CLIENT half. The server compiles the very same words
// into SQL (gallery-service `gallery-manage-list.service.ts` → `searchPredicate`) and owns the
// real search over the whole account; this pass is the residual guard that keeps optimistic and
// mid-flight rows from leaking past a query the server never saw. One grammar, two compilers.

// Nominative and genitive — the two forms a photographer types («июнь» / «июня»).
const MONTH_FORMS: readonly (readonly string[])[] = [
  ['январь', 'января'],
  ['февраль', 'февраля'],
  ['март', 'марта'],
  ['апрель', 'апреля'],
  ['май', 'мая'],
  ['июнь', 'июня'],
  ['июль', 'июля'],
  ['август', 'августа'],
  ['сентябрь', 'сентября'],
  ['октябрь', 'октября'],
  ['ноябрь', 'ноября'],
  ['декабрь', 'декабря'],
];

export interface SearchableGallery {
  title: string;
  shootDate: string | null;
  /** День создания — им галерея ищется без даты съёмки. */
  createdAt?: string;
}

interface CalendarDay {
  year: number;
  month: number;
  day: number;
}

type DayMatcher = (date: CalendarDay) => boolean;

// A word is a month only when it PREFIXES a month form, three letters minimum: «мар» is
// март, «маркетинг» is not, and «ию» stays plain text because it names two months.
function monthOf(word: string): number | null {
  if (word.length < 3) return null;
  const index = MONTH_FORMS.findIndex(forms => forms.some(form => form.startsWith(word)));
  return index < 0 ? null : index + 1;
}

function isRealDay(year: number, month: number, day: number): boolean {
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

// The gallery's date is its shoot date, or its creation day when the shoot date is unset —
// the same coalesce the server's date column does. The creation timestamp reads as the LOCAL
// calendar day, which is the day the card itself prints (`formatShortDate`).
function dayOf(item: SearchableGallery): CalendarDay | null {
  const shoot = /^(\d{4})-(\d{2})-(\d{2})$/.exec(item.shootDate ?? '');
  if (shoot) return { year: Number(shoot[1]), month: Number(shoot[2]), day: Number(shoot[3]) };
  const created = new Date(item.createdAt ?? '');
  if (Number.isNaN(created.getTime())) return null;
  return { year: created.getFullYear(), month: created.getMonth() + 1, day: created.getDate() };
}

// The date forms a single word can carry: dd.mm.yyyy · a bare year · a month name.
// A word that is none of them is plain text.
function dateAtom(word: string): DayMatcher | null {
  const dmy = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(word);
  if (dmy) {
    const [day, month, year] = [Number(dmy[1]), Number(dmy[2]), Number(dmy[3])];
    // «31.02.2025» is no day at all — it falls back to being ordinary text.
    return isRealDay(year, month, day) ? date => date.year === year && date.month === month && date.day === day : null;
  }

  if (/^\d{4}$/.test(word)) {
    const year = Number(word);
    return date => date.year === year;
  }

  const month = monthOf(word);
  return month === null ? null : date => date.month === month;
}

// «июня 12» and «12 июня» are two words meaning ONE day, so they are read together
// instead of word by word.
function dayAndMonth(words: string[]): DayMatcher | null {
  if (words.length !== 2) return null;
  const orders: Array<[string, string]> = [
    [words[0], words[1]],
    [words[1], words[0]],
  ];
  for (const [monthWord, dayWord] of orders) {
    const month = monthOf(monthWord);
    const day = /^\d{1,2}$/.test(dayWord) ? Number(dayWord) : 0;
    if (month !== null && day >= 1 && day <= 31) return date => date.month === month && date.day === day;
  }

  return null;
}

/**
 * Every word must match — that is how adding a word narrows the list. A word matches the
 * title, or the gallery's date when the word reads as a date. An empty query matches everything.
 */
export function matchesSearchQuery(item: SearchableGallery, query: string): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const date = dayOf(item);
  const oneDay = dayAndMonth(words);
  if (oneDay) return date !== null && oneDay(date);
  const title = item.title.toLowerCase();
  return words.every(word => {
    const atom = dateAtom(word);
    return title.includes(word) || (atom !== null && date !== null && atom(date));
  });
}
